// src/components/user/Masivo/DeliveryEditModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Package } from 'lucide-react';
import { validateDelivery } from '../../../utils/deliveryValidator';

function DeliveryEditModal({ delivery, onSave, onClose, validDistricts, userProducts }) {
  const [formData, setFormData] = useState(delivery.data);
  const [localErrors, setLocalErrors] = useState([]);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  useEffect(() => {
    setFormData(delivery.data);
  }, [delivery]);

  function handleChange(field, value) {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setLocalErrors([]);
  }

  function handleSave() {
    const validation = validateDelivery(formData, delivery.rowNumber, validDistricts);

    if (!validation.isValid) {
      setLocalErrors(validation.errors);
      return;
    }

    onSave({
      ...delivery,
      ...validation,
      data: validation.data,
    });
    onClose();
  }

  function handleProductSelect(product) {
    setFormData(prev => {
      const nextSlot = [1, 2, 3, 4, 5].find(index => !prev[`SKU ${index}`]) || 1;
      return {
        ...prev,
        [`SKU ${nextSlot}`]: product.sku,
        [`Cantidad ${nextSlot}`]: prev[`Cantidad ${nextSlot}`] || '1',
      };
    });
    setShowProductPicker(false);
    setProductSearch('');
  }

  const filteredProducts = (userProducts || []).filter(p =>
    p.title.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.sku.toLowerCase().includes(productSearch.toLowerCase())
  );

  const districtsByZone = validDistricts.districts_by_zone || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center">
              <Package className="w-5 h-5 text-brand-primary" />
            </div>
            <h3 className="text-lg font-bold text-brand-primary font-montserrat">
              Editar Fila {delivery.rowNumber}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {localErrors.length > 0 && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-red-900 font-montserrat mb-2">
                    Corrige estos errores:
                  </p>
                  <ul className="space-y-1">
                    {localErrors.map((err, idx) => (
                      <li key={idx} className="text-sm text-red-700 font-montserrat">
                        - {err.message}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              Nombre y Apellido <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData['Nombre y Apellido'] || ''}
              onChange={e => handleChange('Nombre y Apellido', e.target.value)}
              className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
              placeholder="Juan Perez"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              WhatsApp (9 digitos) <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={formData['WhatsApp'] || ''}
              onChange={e => handleChange('WhatsApp', e.target.value.replace(/\D/g, '').slice(0, 9))}
              className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
              placeholder="987654321"
              maxLength={9}
            />
            <p className="text-xs text-slate-500 font-montserrat mt-1">
              Solo numeros, sin codigo de pais
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              Distrito <span className="text-red-500">*</span>
            </label>
            <select
              value={formData['Distrito'] || ''}
              onChange={e => handleChange('Distrito', e.target.value)}
              className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
            >
              <option value="">Seleccionar distrito...</option>
              {Object.entries(districtsByZone).map(([zone, districts]) => (
                <optgroup key={zone} label={zone}>
                  {districts.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              Direccion <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData['Direccion'] || ''}
              onChange={e => handleChange('Direccion', e.target.value)}
              className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat resize-none"
              placeholder="Av. Larco 123, Piso 4"
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              Productos / SKUs <span className="text-red-500">*</span>
            </label>

            {userProducts && userProducts.length > 0 && (
              <button
                type="button"
                onClick={() => setShowProductPicker(!showProductPicker)}
                className="mb-2 text-sm text-brand-primary hover:underline font-montserrat"
              >
                {showProductPicker ? 'Cerrar' : 'Elegir de mi inventario'}
              </button>
            )}

            {showProductPicker && (
              <div className="mb-3 border-2 border-brand-primary/20 rounded-lg p-3 bg-slate-50">
                <input
                  type="text"
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  placeholder="Buscar producto..."
                  className="w-full px-3 py-2 border rounded-lg mb-2 font-montserrat text-sm"
                />
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {filteredProducts.map(product => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => handleProductSelect(product)}
                      className="w-full text-left px-3 py-2 hover:bg-white rounded-lg transition-colors border border-transparent hover:border-brand-primary/30"
                    >
                      <p className="text-sm font-semibold text-slate-900 font-montserrat">
                        {product.title}
                      </p>
                      <p className="text-xs text-slate-600 font-montserrat">
                        SKU: {product.sku} - S/ {product.price} - Stock: {product.available_quantity ?? 0}
                      </p>
                    </button>
                  ))}
                  {filteredProducts.length === 0 && (
                    <p className="text-sm text-slate-500 font-montserrat text-center py-4">
                      No se encontraron productos
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(index => (
                <div key={index} className="grid grid-cols-[1fr_110px] gap-3">
                  <div>
                    <input
                      type="text"
                      value={formData[`SKU ${index}`] || ''}
                      onChange={e => handleChange(`SKU ${index}`, e.target.value)}
                      className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
                      placeholder={`SKU ${index}`}
                    />
                    <p className="text-xs text-slate-500 font-montserrat mt-1">
                      {index === 1 ? 'SKU obligatorio' : `SKU adicional ${index}`}
                    </p>
                  </div>
                  <div>
                    <input
                      type="number"
                      value={formData[`Cantidad ${index}`] || (index === 1 ? '1' : '')}
                      onChange={e => handleChange(`Cantidad ${index}`, e.target.value)}
                      className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
                      min="1"
                    />
                    <p className="text-xs text-slate-500 font-montserrat mt-1">Cantidad</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 font-montserrat mt-1">
              Los nombres de producto se toman de la base de datos usando el SKU.
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              Cobrar (S/)
            </label>
            <input
              type="number"
              value={formData['Cobrar'] || '0'}
              onChange={e => handleChange('Cobrar', e.target.value)}
              className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
              step="0.01"
              min="0"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              Notas
            </label>
            <textarea
              value={formData['Notas'] || ''}
              onChange={e => handleChange('Notas', e.target.value)}
              className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat resize-none"
              placeholder="Llamar antes de llegar"
              rows={2}
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border-2 border-slate-300 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition-colors font-montserrat"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-3 bg-brand-primary text-white rounded-xl font-semibold hover:bg-brand-primary/90 transition-colors font-montserrat flex items-center justify-center gap-2"
          >
            <Save className="w-5 h-5" />
            <span>Guardar Cambios</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeliveryEditModal;
